/**
 * history.ts
 * ----------------
 * Reducers related to live site event history used across all portals.
 */

import { LOCATION_CHANGE } from 'connected-react-router';
import { Record } from 'immutable';

import { Action, AsyncAction, PayloadAction } from '../../actions/types';
import {
    PreloadActiveLiveSiteEvents, PreloadResolvedLiveSiteEvents, Shared_ClearHistoryViewContext,
    Shared_ReceiveGetActiveLiveSiteEvents, Shared_ReceiveGetResolvedLiveSiteEvents,
    Shared_RequestGetActiveLiveSiteEvents, Shared_RequestGetResolvedLiveSiteEvents, Shared_SetHistoryViewContext,
} from '../../constants/actions';
import { Status } from '../../constants/statuses';
import { QueryLiveSiteEventsResponse } from '../../models/dto/updatePortal';
import {
    PreloadActiveLiveSiteEventsPayload, PreloadResolvedLiveSiteEventsPayload,
} from '../../models/payloads/preload';
import { HistoryViewContextPayload } from '../../models/payloads/shared';
import { LiveSiteEventSeverity } from '../../models/status';
import { TimeRangeValue } from '../../models/ui/status';
import { asyncRequestReducer } from '../shared/common';
import { isSetEquivalent } from '../../utilities/array';
import { difference, getLocalDateFromUtcTimestamp } from '../../utilities/time';

interface HistoryStateFields {
    activeLiveSiteEventsResponse: QueryLiveSiteEventsResponse | undefined;
    geographies: string[];
    getActiveLiveSiteEventsError: string;
    getActiveLiveSiteEventsStatus: Status;
    getResolvedLiveSiteEventsError: string;
    getResolvedLiveSiteEventsStatus: Status;
    resolvedLiveSiteEventsResponse: QueryLiveSiteEventsResponse | undefined;
    search: string;
    services: string[];
    severities: LiveSiteEventSeverity[];
    time: TimeRangeValue;
}

/**
 * Application substate
 */

export class HistoryState extends Record<HistoryStateFields>({
    activeLiveSiteEventsResponse: undefined,
    geographies: [],
    getActiveLiveSiteEventsError: '',
    getActiveLiveSiteEventsStatus: 'none',
    getResolvedLiveSiteEventsError: '',
    getResolvedLiveSiteEventsStatus: 'none',
    resolvedLiveSiteEventsResponse: undefined,
    search: '',
    services: [],
    severities: [],
    time: 'Last7Days',
}) { }

const initialState = new HistoryState();

/**
 * Private variables
 */

const fiveMinutesInMilliseconds = 5 * 60 * 1000;

function clearHistoryViewContextReducer (state: HistoryState) {
    let nextState = state
        .set('getActiveLiveSiteEventsError', initialState.getActiveLiveSiteEventsError)
        .set('getActiveLiveSiteEventsStatus', initialState.getActiveLiveSiteEventsStatus)
        .set('getResolvedLiveSiteEventsError', initialState.getResolvedLiveSiteEventsError)
        .set('getResolvedLiveSiteEventsStatus', initialState.getResolvedLiveSiteEventsStatus)
        .set('search', initialState.search)
        .set('time', initialState.time);

    if (state.activeLiveSiteEventsResponse !== initialState.activeLiveSiteEventsResponse) {
        nextState = nextState.set('activeLiveSiteEventsResponse', initialState.activeLiveSiteEventsResponse);
    }

    if (!isSetEquivalent(state.geographies, initialState.geographies)) {
        nextState = nextState.set('geographies', initialState.geographies);
    }

    if (state.resolvedLiveSiteEventsResponse !== initialState.resolvedLiveSiteEventsResponse) {
        nextState = nextState.set('resolvedLiveSiteEventsResponse', initialState.resolvedLiveSiteEventsResponse);
    }

    if (!isSetEquivalent(state.services, initialState.services)) {
        nextState = nextState.set('services', initialState.services);
    }

    if (!isSetEquivalent(state.severities, initialState.severities)) {
        nextState = nextState.set('severities', initialState.severities);
    }

    return nextState;
}

function locationChangeReducer (state: HistoryState) {
    // Note: for now, we listen to location changes on any route to check staleness. We can receive and check the action
    // data in the future if we only want to update on certain location changes.
    const { activeLiveSiteEventsResponse, resolvedLiveSiteEventsResponse } = state;

    let nextState = state;
    const now = new Date();

    if (activeLiveSiteEventsResponse !== undefined) {
        const lastQueriedTime = getLocalDateFromUtcTimestamp(activeLiveSiteEventsResponse.queriedTimestamp);
        const activeQueryTimestampDiff = difference(now, lastQueriedTime);

        nextState = activeQueryTimestampDiff > fiveMinutesInMilliseconds
            ? nextState
                .set('getActiveLiveSiteEventsError', initialState.getActiveLiveSiteEventsError)
                .set('getActiveLiveSiteEventsStatus', initialState.getActiveLiveSiteEventsStatus)
            : nextState;
    }

    if (resolvedLiveSiteEventsResponse !== undefined) {
        const lastQueriedTime = getLocalDateFromUtcTimestamp(resolvedLiveSiteEventsResponse.queriedTimestamp);
        const resolvedQueryTimestampDiff = difference(now, lastQueriedTime);

        nextState = resolvedQueryTimestampDiff > fiveMinutesInMilliseconds
            ? nextState
                .set('getResolvedLiveSiteEventsError', initialState.getResolvedLiveSiteEventsError)
                .set('getResolvedLiveSiteEventsStatus', initialState.getResolvedLiveSiteEventsStatus)
            : nextState;
    }

    return nextState;
}

function preloadActiveLiveSiteEventsReducer (state: HistoryState, action: PayloadAction<PreloadActiveLiveSiteEventsPayload>) {
    const { payload } = action;

    return state
        .set('getActiveLiveSiteEventsError', initialState.getActiveLiveSiteEventsError)
        .set('getActiveLiveSiteEventsStatus', 'succeeded')
        .set('activeLiveSiteEventsResponse', payload);
}

function preloadResolvedLiveSiteEventsReducer (state: HistoryState, action: PayloadAction<PreloadResolvedLiveSiteEventsPayload>) {
    const { payload } = action;

    return state
        .set('getResolvedLiveSiteEventsError', initialState.getResolvedLiveSiteEventsError)
        .set('getResolvedLiveSiteEventsStatus', 'succeeded')
        .set('resolvedLiveSiteEventsResponse', payload);
}

function receiveGetActiveLiveSiteEventsReducer (state: HistoryState, action: PayloadAction<QueryLiveSiteEventsResponse>) {
    const { payload } = action;

    return state.set('activeLiveSiteEventsResponse', payload);
}

function receiveGetResolvedLiveSiteEventsReducer (state: HistoryState, action: PayloadAction<QueryLiveSiteEventsResponse>) {
    const { payload } = action;

    return state.set('resolvedLiveSiteEventsResponse', payload);
}

const requestGetActiveLiveSiteEventsReducer = asyncRequestReducer<HistoryState>(
    (state, error) => state.set('getActiveLiveSiteEventsError', error),
    (state, status) => state.set('getActiveLiveSiteEventsStatus', status)
);

const requestGetResolvedLiveSiteEventsReducer = asyncRequestReducer<HistoryState>(
    (state, error) => state.set('getResolvedLiveSiteEventsError', error),
    (state, status) => state.set('getResolvedLiveSiteEventsStatus', status)
);

function setHistoryViewContextReducer (state: HistoryState, action: PayloadAction<HistoryViewContextPayload>) {
    const { payload } = action;
    const { geographies, search, services, severities, time } = payload;

    let nextState = state;

    if (!isSetEquivalent(geographies, state.geographies)) {
        nextState = nextState.set('geographies', geographies);
    }

    if (search !== state.search) {
        nextState = nextState.set('search', search);
    }

    if (!isSetEquivalent(services, state.services)) {
        nextState = nextState.set('services', services);
    }

    if (!isSetEquivalent(severities, state.severities)) {
        nextState = nextState.set('severities', severities);
    }

    if (time !== state.time) {
        nextState = nextState
            .set('getResolvedLiveSiteEventsError', initialState.getResolvedLiveSiteEventsError)
            .set('getResolvedLiveSiteEventsStatus', initialState.getResolvedLiveSiteEventsStatus)
            .set('time', time);
    }

    return nextState;
}

/**
 * Reducer that updates the application state in response to live site event history-related actions.
 * @param state Current application state
 * @param action Action to be performed on state
 * @returns Updated application state
 */
export function historyReducer (state: HistoryState = initialState, action?: Action) {
    if (action === undefined) {
        return state;
    }

    switch (action.type) {
        case LOCATION_CHANGE:
            return locationChangeReducer(state);

        case PreloadActiveLiveSiteEvents:
            return preloadActiveLiveSiteEventsReducer(state, action as PayloadAction<PreloadActiveLiveSiteEventsPayload>);

        case PreloadResolvedLiveSiteEvents:
            return preloadResolvedLiveSiteEventsReducer(state, action as PayloadAction<PreloadResolvedLiveSiteEventsPayload>);

        case Shared_ClearHistoryViewContext:
            return clearHistoryViewContextReducer(state);

        case Shared_ReceiveGetActiveLiveSiteEvents:
            return receiveGetActiveLiveSiteEventsReducer(state, action as PayloadAction<QueryLiveSiteEventsResponse>);

        case Shared_ReceiveGetResolvedLiveSiteEvents:
            return receiveGetResolvedLiveSiteEventsReducer(state, action as PayloadAction<QueryLiveSiteEventsResponse>);

        case Shared_RequestGetActiveLiveSiteEvents:
            return requestGetActiveLiveSiteEventsReducer(state, action as AsyncAction);

        case Shared_RequestGetResolvedLiveSiteEvents:
            return requestGetResolvedLiveSiteEventsReducer(state, action as AsyncAction);

        case Shared_SetHistoryViewContext:
            return setHistoryViewContextReducer(state, action as PayloadAction<HistoryViewContextPayload>);

        default:
            return state;
    }
}
